import { useEffect, useState } from "react";
import { Box, Button, Link, Typography, Modal, TextField } from "@mui/material";
import { useMutation } from "@apollo/client";
import { MAILCHIMP_REQUEST_PASSWORD_RESET } from "../../graphQL/mutations";
import ValidEmail from "../../utils/helpers/ValidEmail";
import ExtractSubdomain from "../../utils/helpers/ExtractSubdomain";
import CircularLoading from "../loading/CircularLoading";
import AlertErrors from "../errors/AlertErrors";

const styles = {
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    display: "flex",
    flexDirection: "column",
    boxShadow: 24,
    p: 4,
  },
};

export default function ForgotPassword({ passwordResetRequired }) {
  const [open, setOpen] = useState(passwordResetRequired);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [email, setEmail] = useState("");
  const [requestPasswordReset, { error }] = useMutation(MAILCHIMP_REQUEST_PASSWORD_RESET);

  useEffect(() => {
    setOpen(passwordResetRequired);
  }, [passwordResetRequired]);

  // ===========================ALERT===========================
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openFailure, setOpenFailure] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const [submitLoading, setSubmitLoading] = useState(false);
  const handleSubmit = async () => {
    setSubmitLoading(true);
    if (!ValidEmail(email)) {
      setErrorMessage("Please enter a valid email");
      setOpenSuccess(false);
      setOpenFailure(true);
    } else {
      console.log(email);
      try {
        const mutationResponse = await requestPasswordReset({
          variables: {
            userEmail: email,
            branding: ExtractSubdomain(window.location.hostname),
          },
        });
        setOpenFailure(false);
        setOpenSuccess(true);
      } catch (e) {
        if (
          e?.graphQLErrors[0]?.originalError?.message?.includes(
            "Mutation requestPasswordReset error: email not found"
          )
        ) {
          console.log("Email not found in our system", e);
          setErrorMessage("No account associated with this email");
          setOpenSuccess(false);
          setOpenFailure(true);
        } else {
          console.log("Request Password Reset Fail", e);
          setErrorMessage("Password reset failed. Please try again");
          setOpenSuccess(false);
          setOpenFailure(true);
          setSubmitLoading(false);
          setTimeout(() => {
            setOpenFailure(false);
            setOpenSuccess(false);
          }, 5000);
          throw new Error("Error requestPasswordReset fail: ", e);
        }
      }
    }
    setSubmitLoading(false);
    setTimeout(() => {
      setOpenFailure(false);
      setOpenSuccess(false);
    }, 5000);
  };

  return (
    <Box>
      <Link
        underline="hover"
        sx={{
          ":hover": {
            cursor: "pointer",
          },
          color: "secondary.main",
          mt: 0.5,
        }}
        // variant="body2"
        onClick={handleOpen}
      >
        <Typography
          id="forgotPassword"
          sx={{ color: "secondary.main" }}
          variant="body2"
          data-cy="forgot-password"
        >
          Forgot Password?
        </Typography>
      </Link>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...styles.modal }}>
          <Typography variant="h5" sx={{ mb: 2, width: "286px" }}>
            {passwordResetRequired ? "Reset Password Required" : "Reset Password"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, width: "286px" }}>
            Please enter your email address and we will send you instructions to reset your
            password.
          </Typography>
          <TextField
            required
            label="Email"
            id="email"
            data-cy="email-reset-password"
            placeholder="Email"
            sx={{ mb: 2, width: "286px" }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Box
            sx={{ display: "flex", flexDirection: "column", width: "286px", alignItems: "center" }}
          >
            <Button
              id="sendPasswordReset"
              variant="Button1"
              onClick={handleSubmit}
              disabled={submitLoading}
              sx={{ width: "inherit" }}
              data-cy="send-reset-password"
            >
              Send Reset Link
            </Button>
            {submitLoading && (
              <CircularLoading
                loaderSx={{ position: "absolute", mt: 1 }}
                loading={false}
                size={24}
              />
            )}
          </Box>
          <Box sx={{ mt: 1, width: "286px" }}>
            <AlertErrors
              openFailure={openFailure}
              openSuccess={openSuccess}
              failureMessage={errorMessage}
              successMessage={"Reset Link Sent!"}
              setOpenFailure={setOpenFailure}
              setOpenSuccess={setOpenSuccess}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
